import React, {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
} from '@tanstack/react-table'
import { RefObject, useCallback, useMemo } from 'react'
import { ReactComponent as ResetColumnsSize } from '../../assets/icons/table/reset-columns.svg'

import {
	CheckCircleOutline,
	HighlightOffOutlined,
	InfoOutlined,
	KeyboardArrowDown,
	KeyboardArrowUp,
	UnfoldMore,
} from '@mui/icons-material'
import { IconButton, Tooltip } from '../../components/ui'
import { useUserDateFormat } from '../useUserDateFormat'
import cn from '../../lib/utils/cn'
import { shortenFileName } from '../../lib/utils/formatDocument'
import { AuditEvent, AuditScheme } from '../../lib/api/types/audit.interfaces'
import { AdminAuditDataTableEntry } from '../../pages/Admin/components/AdminDataTable/interfaces'

type AuditOrderBy = 'date' | 'type' | 'objectType' | 'userName' | 'objectName' | null
type AuditOrderDirection = -1 | 1 | null

interface useCreateAdminAuditDataTableProps {
	data?: AuditEvent[] | null
	auditScheme?: AuditScheme | null
	containerRef?: RefObject<HTMLDivElement>
	searchQuery?: string
	isMobile?: boolean
	onOpenEventInfo: (id: string) => void
	orderBy?: AuditOrderBy
	orderDirection?: AuditOrderDirection
	onSortChange?: (orderBy: AuditOrderBy, orderDirection: AuditOrderDirection) => void
}

export const useCreateAdminAuditDataTable = ({
	data = [],
	auditScheme,
	isMobile,
	onOpenEventInfo,
	orderBy,
	orderDirection,
	onSortChange,
}: useCreateAdminAuditDataTableProps) => {
	const { formatDate, formatDateTime } = useUserDateFormat()
	const handleSortClick = useCallback(
		(columnId: AuditOrderBy) => {
			if (!onSortChange) return

			let newDirection: AuditOrderDirection = 1
			let newOrderBy: AuditOrderBy = columnId

			if (columnId === orderBy) {
				if (orderDirection === 1) {
					newDirection = -1
				} else if (orderDirection === -1) {
					newDirection = null
					newOrderBy = null
				}
			}

			onSortChange(newOrderBy, newDirection)
		},
		[onSortChange, orderBy, orderDirection],
	)

	const renderSortingHeader = useCallback(
		(title: string, columnId: AuditOrderBy, className?: string) => {
			const isActive = columnId === orderBy
			const isAsc = isActive && orderDirection === 1
			const isDesc = isActive && orderDirection === -1

			return (
				<div
					className={cn(
						'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 dark:text-text-table-dark min-h-full',
						className
					)}
					onClick={() => handleSortClick(columnId)}
				>
					<span className="text-center w-fit truncate">{title}</span>
					<span
						className={cn(
							'flex items-center justify-center transition-all duration-200 w-3 h-5',
							!isActive && 'hidden group-hover:inline-block'
						)}
					>
						{isAsc && <KeyboardArrowUp className="w-5 h-5" />}
						{isDesc && <KeyboardArrowDown className="w-5 h-5" />}
						{!isActive && (
							<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
						)}
					</span>
				</div>
			)
		},
		[handleSortClick, orderBy, orderDirection],
	)

	const tableData: AdminAuditDataTableEntry[] = useMemo(() => {
		return (data ?? []).map((event) => {
			const eventDefinition = auditScheme?.events.find(
				(e) => e.type === event.type
			)
			const category = auditScheme?.categories.find(
				(c) => c.type === eventDefinition?.category
			)

			return {
				id: event?.id,
				date: new Date(event?.date),
				type: eventDefinition?.description || `Unknown Type (${event.type})`,
				status: event?.isSuccessful,
				category:
					category?.name || `Unknown Category (${eventDefinition?.category})`,
				userName: event?.userName,
				objectName: event?.objectName,
				options: {
					id: event?.id,
				},
				objectId: event?.objectId ?? '-',
				userId: event?.userId ?? '-',
			}
		})
	}, [auditScheme, data])

	const columns = useMemo<ColumnDef<AdminAuditDataTableEntry>[]>(() => {
		const base: ColumnDef<AdminAuditDataTableEntry>[] = [
			{
				accessorKey: 'date',
				enableResizing: true,
				minSize: 80,
				size: 100,
				header: () => renderSortingHeader('Event Date', 'date'),
				cell: (info) => {
					const val = info.getValue()
					return (
						<Tooltip label={val ? formatDateTime(String(val)) : ''} classNameChildrenWrapper="w-fit">
							<span className='dark:text-text-table-dark'>
								{val ? formatDateTime(String(val)) : '-'}
							</span>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'type',
				enableResizing: true,
				minSize: 100,
				size: 130,
				header: () => renderSortingHeader('Event Type', 'type'),
				cell: ({ row }) => {
					return (
						<p className="flex items-center gap-1 dark:text-text-table-dark">
							<span>
								{row?.original?.status ? (
									<CheckCircleOutline className="fill-positive w-5 h-5" />
								) : (
									<HighlightOffOutlined className="fill-negative w-5 h-5" />
								)}
							</span>
							{row?.original?.type}{' '}
						</p>
					)
				},
			},
			{
				accessorKey: 'category',
				enableResizing: true,
				minSize: 90,
				header: () => renderSortingHeader('Object Type', 'objectType'),
				cell: (info) =><span className='dark:text-text-table-dark'> {String(info.getValue() ?? '')}</span>,
			},
			{
				accessorKey: 'userName',
				enableResizing: true,
				minSize: 90,
				header: () => renderSortingHeader('User Name', 'userName'),
				cell: (info) => {
					const value = String(info.getValue() ?? '')
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-table-dark">
							<Tooltip
								label={value}
								className={cn(
									'max-w-[100px] text-wrap',
									isMobile && 'opacity-0'
								)}
							>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(value) : value}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'objectName',
				enableResizing: true,
				minSize: 90,
				header: () => renderSortingHeader('Object Name', 'objectName'),
				cell: (info) => {
					const value = String(info.getValue() ?? '')
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-table-dark">
							<Tooltip
								label={value}
								className={cn(
									'w-fit',
									isMobile && 'opacity-0 w-fit h-auto text-wrap'
								)}
							>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(value) : value}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'options',
				enableResizing: false,
				enableSorting: false,
				size: 50,
				maxSize: 50,
				minSize: 25,
				header: () => (
					<div className="flex items-center justify-end gap-1">
						<IconButton
							variant="transparent"
							className="w-8 h-8 group hover:bg-transparent"
							tooltipPosition="left"
							icon={
								<ResetColumnsSize className="fill-text-table w-5 h-5 group-hover:fill-accent-hover dark:fill-text-table-dark dark:group-hover:fill-accent-hover" />
							}
							onClick={() => table.resetColumnSizing()}
							tooltip={'Reset columns sizes'}
						/>
					</div>
				),
				cell: ({ row }) => {
					return (
						<div className="bg-inherit md:absolute right-0 top-0 bottom-0 flex items-center justify-end gap-2 w-fit md:ml-auto pl-4 md:pr-2 h-full group-hover:lg:left-custom-shadow opacity-0 md:opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all duration-150 ease-in-out">
							<IconButton
								className="w-8 h-8 rounded-lg"
								variant="secondary"
								icon={<InfoOutlined className="w-4 dark:fill-text-main-dark fill-text-main" />}
								onClick={() => onOpenEventInfo(row.original.id)}
								tooltip={'Event info'}
							/>
						</div>
					)
				},
			},
		]
		return base
	}, [formatDateTime, isMobile, onOpenEventInfo, renderSortingHeader])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
	})

	// const table = useReactTable({
	// 	data: tableData,
	// 	columns,
	// 	columnResizeMode: 'onChange',
	// 	getCoreRowModel: getCoreRowModel(),
	// 	manualSorting: false,
	// 	enableSorting: true,
	// 	enableSortingRemoval: true,
	// 	state: {
	// 		pagination: {
	// 			pageSize: limit,
	// 			pageIndex: offset / limit,
	// 		},
	// 	},
	// })

	return { table }
}
